import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { AdminScope } from '../config/adminScope';
import { ADMIN_HOME_PATH } from '../config/adminScope';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || '';

type AdminScopeContextValue = {
  scope: AdminScope;
  loading: boolean;
  userId: string;
  email: string;
  displayName: string;
  homePath: string;
};

const AdminScopeContext = createContext<AdminScopeContextValue>({
  scope: 'limited',
  loading: true,
  userId: '',
  email: '',
  displayName: '',
  homePath: ADMIN_HOME_PATH.limited,
});

export const AdminScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [scope, setScope] = useState<AdminScope>('limited');
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/auth/me`, { credentials: 'include' });
        const json = await res.json().catch(() => ({}));
        if (cancelled || !json?.success) return;

        const data = json.data || {};
        const resolvedScope: AdminScope = data.adminScope === 'full' ? 'full' : 'limited';
        setScope(resolvedScope);
        setUserId(data.userId || '');
        setEmail(data.email || '');
        setDisplayName(data.nickname || data.email || 'Admin');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      scope,
      loading,
      userId,
      email,
      displayName,
      homePath: ADMIN_HOME_PATH[scope],
    }),
    [scope, loading, userId, email, displayName]
  );

  return <AdminScopeContext.Provider value={value}>{children}</AdminScopeContext.Provider>;
};

export function useAdminScope() {
  return useContext(AdminScopeContext);
}
